import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/contactus.scss";

const initialFormData = {
  firstName: "",
  lastName: "",
  email: "",
  subject: "",
  message: "",
};

function Contact() {
  const [formData, setFormData] = useState(initialFormData);
  const [submitState, setSubmitState] = useState("idle");
  const [errors, setErrors] = useState({});
  const submitTimer = useRef(null);
  const statusRef = useRef(null);

  useEffect(
    () => () => {
      if (submitTimer.current) window.clearTimeout(submitTimer.current);
    },
    []
  );

  useEffect(() => {
    if (submitState === "success") statusRef.current?.focus();
  }, [submitState]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
    setErrors((current) => {
      if (!current[name]) return current;
      const nextErrors = { ...current };
      delete nextErrors[name];
      return nextErrors;
    });
    if (submitState === "success") setSubmitState("idle");
  };

  const validateForm = () => {
    const nextErrors = {};
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.firstName.trim())
      nextErrors.firstName = "Enter your first name.";
    if (!formData.lastName.trim())
      nextErrors.lastName = "Enter your last name.";
    if (!formData.email.trim()) {
      nextErrors.email = "Enter your email address.";
    } else if (!emailPattern.test(formData.email.trim())) {
      nextErrors.email = "Enter a valid email address.";
    }
    if (!formData.subject) nextErrors.subject = "Choose a subject.";
    if (!formData.message.trim()) {
      nextErrors.message = "Tell us how we can help.";
    } else if (formData.message.trim().length < 20) {
      nextErrors.message =
        "Please add a little more detail (at least 20 characters).";
    }

    return nextErrors;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (submitState === "submitting") return;

    const nextErrors = validateForm();
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      setSubmitState("error");
      const firstInvalidField = Object.keys(nextErrors)[0];
      window.requestAnimationFrame(() => {
        document.getElementById(firstInvalidField)?.focus();
      });
      return;
    }

    setErrors({});
    setSubmitState("submitting");
    submitTimer.current = window.setTimeout(() => {
      setFormData(initialFormData);
      setSubmitState("success");
      submitTimer.current = null;
    }, 900);
  };

  const fieldError = (fieldName) =>
    errors[fieldName] ? (
      <p className="contact__error" id={`${fieldName}-error`} role="alert">
        {errors[fieldName]}
      </p>
    ) : null;

  const toTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <main className="contact">
      <header className="contact__heading">
        <p className="contact__eyebrow">Client care</p>
        <h1>How can we help?</h1>
        <p>
          Product, sizing, delivery or order questions are welcome. Send a note
          and our team will respond as soon as possible during business hours.
        </p>
      </header>

      <div className="contact__layout">
        <aside className="contact__details" aria-label="Contact information">
          <p className="contact__eyebrow">Direct contact</p>
          <div className="contact__detail">
            <span>Email</span>
            <a href={`mailto:${siteConfig.contact.email}`}>
              {siteConfig.contact.email}
            </a>
          </div>

          <div className="contact__detail">
            <span>Registered office</span>
            <address>
              {siteConfig.legalName}
              <br />
              {siteConfig.contact.addressLines.map((line) => (
                <React.Fragment key={line}>
                  {line}
                  <br />
                </React.Fragment>
              ))}
            </address>
          </div>
          <div className="contact__detail">
            <span>Hours</span>
            <p>Monday–Friday, 09:00–17:30 HKT</p>
          </div>

          <nav className="contact__help-links" aria-label="Order help">
            <Link to="/shipping" onClick={toTop}>
              Shipping information
            </Link>
            <Link to="/returns" onClick={toTop}>
              Returns information
            </Link>
            <Link to="/privacy" onClick={toTop}>
              Privacy notice
            </Link>
          </nav>
        </aside>

        <section
          className="contact__form-panel"
          aria-labelledby="contact-form-title"
        >
          <div className="contact__form-heading">
            <p className="contact__eyebrow">Send a message</p>
            <h2 id="contact-form-title">Tell us what you need.</h2>
          </div>

          {submitState === "success" && (
            <div
              className="contact__success"
              role="status"
              tabIndex="-1"
              ref={statusRef}
            >
              <strong>Thank you for getting in touch.</strong>
              <p>Our client care team will reply within two business days.</p>
            </div>
          )}

          <form className="contact__form" onSubmit={handleSubmit} noValidate>
            <div className="contact__form-row">
              <div className="contact__field">
                <label htmlFor="firstName">First name *</label>
                <input
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  autoComplete="given-name"
                  aria-invalid={Boolean(errors.firstName)}
                  aria-describedby={
                    errors.firstName ? "firstName-error" : undefined
                  }
                />
                {fieldError("firstName")}
              </div>
              <div className="contact__field">
                <label htmlFor="lastName">Last name *</label>
                <input
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  autoComplete="family-name"
                  aria-invalid={Boolean(errors.lastName)}
                  aria-describedby={
                    errors.lastName ? "lastName-error" : undefined
                  }
                />
                {fieldError("lastName")}
              </div>
            </div>

            <div className="contact__form-row">
              <div className="contact__field">
                <label htmlFor="email">Email address *</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  autoComplete="email"
                  inputMode="email"
                  aria-invalid={Boolean(errors.email)}
                  aria-describedby={errors.email ? "email-error" : undefined}
                />
                {fieldError("email")}
              </div>
            </div>

            <div className="contact__field">
              <label htmlFor="subject">Subject *</label>
              <select
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                aria-invalid={Boolean(errors.subject)}
                aria-describedby={errors.subject ? "subject-error" : undefined}
              >
                <option value="">Choose a subject</option>
                <option value="product">Product and sizing</option>
                <option value="order">Order support</option>
                <option value="delivery">Delivery</option>
                <option value="return">Return or refund</option>
                <option value="press">Press and partnerships</option>
                <option value="other">Something else</option>
              </select>
              {fieldError("subject")}
            </div>

            <div className="contact__field">
              <label htmlFor="message">Message *</label>
              <textarea
                id="message"
                name="message"
                rows="7"
                maxLength="1200"
                value={formData.message}
                onChange={handleInputChange}
                aria-invalid={Boolean(errors.message)}
                aria-describedby={
                  errors.message ? "message-error" : "message-help"
                }
              />
              <div className="contact__field-meta" id="message-help">
                <span>Include an order number when relevant.</span>
                <span>{formData.message.length}/1200</span>
              </div>
              {fieldError("message")}
            </div>

            {submitState === "error" && (
              <p className="contact__form-error" role="alert">
                Review the highlighted fields before sending your message.
              </p>
            )}

            <button
              className="contact__submit"
              type="submit"
              disabled={submitState === "submitting"}
            >
              {submitState === "submitting" ? "Sending…" : "Send message"}
            </button>
          </form>
        </section>
      </div>
    </main>
  );
}

export default Contact;
